import { MrfiktivUserAvatarViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { AppContextEnum } from "@/lib/enum/appContext.enum";

/**
 * The minimal user shape an avatar needs. Satisfied by IUser, IShortUser and IPartnerUser.
 */
export interface IUserAvatarSource {
  firstName?: string;
  lastName?: string;
  avatar?: MrfiktivUserAvatarViewModelGen;
}

/**
 * The name of a stored variant, e.g. "large" for the 512 px one.
 */
export type UserAvatarTier = keyof MrfiktivUserAvatarViewModelGen;

/**
 * The stored square sizes in px, matching the tiers the backend generates, smallest first.
 */
export const USER_AVATAR_TIERS: { key: UserAvatarTier; size: number }[] = [
  { key: "thumbnail", size: 64 },
  { key: "small", size: 128 },
  { key: "medium", size: 256 },
  { key: "large", size: 512 }
];

/**
 * Picks the smallest stored variant that is still sharp for the rendered size on the given screen density.
 * Falls back to the largest available variant, and to an empty string when the user has no picture.
 *
 * @param avatar the stored variants of the user
 * @param sizePx the rendered size in css px
 * @param devicePixelRatio the screen density, defaults to the current window
 */
export function pickUserAvatarSource(
  avatar: MrfiktivUserAvatarViewModelGen | undefined,
  sizePx: number,
  devicePixelRatio = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1
): string {
  if (!avatar) {
    return "";
  }

  const devicePixels = sizePx * (devicePixelRatio || 1);
  const available = USER_AVATAR_TIERS.filter((tier) => !!avatar[tier.key]);
  const fitting = available.find((tier) => tier.size >= devicePixels) ?? available[available.length - 1];

  return fitting ? avatar[fitting.key] : "";
}

/**
 * Picks a stored variant by name, no matter the rendered size, for placements that ask for a
 * specific one, e.g. a picture that ends up scaled up on print.
 * Falls back to the next larger variant when the asked for one is missing, and to an empty string
 * when the user has no picture.
 *
 * @param avatar the stored variants of the user
 * @param tier the name of the wanted variant
 */
export function pickUserAvatarSourceByTier(
  avatar: MrfiktivUserAvatarViewModelGen | undefined,
  tier: UserAvatarTier
): string {
  const size = USER_AVATAR_TIERS.find((t) => t.key === tier)?.size;

  return size ? pickUserAvatarSource(avatar, size, 1) : "";
}

/**
 * Whether the user has a picture at all, i.e. at least one stored variant.
 */
export function hasUserAvatar(user?: IUserAvatarSource): boolean {
  return USER_AVATAR_TIERS.some((tier) => !!user?.avatar?.[tier.key]);
}

/**
 * First letters of first and last name, shown when a user has no picture.
 */
export function getUserInitials(user?: IUserAvatarSource): string {
  return `${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`;
}

/**
 * The avatar endpoints only exist on the mrfiktiv backend so far, the thg backend follows later
 */
export function isUserAvatarContext(appContext: AppContextEnum): boolean {
  return [AppContextEnum.REPORT_PORTAL, AppContextEnum.REPORT].includes(appContext);
}
